import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { rolesApi } from "../../infrastructure/api/RolesApi";
import type { PermissionGroup } from "../../infrastructure/api/mapper";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permissionGroups: PermissionGroup[];
  onSuccess?: () => void;
}

export const CreateRoleDialog = ({
  open,
  onOpenChange,
  permissionGroups,
  onSuccess,
}: Props) => {
  const { t } = useTranslation("rolesPermissions");
  const [roleName, setRoleName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [createRole, { isLoading }] = rolesApi.useCreateRoleMutation();

  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const togglePermission = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const handleGroupChange = (title: string, checked: boolean) => {
    const group = permissionGroups.find((g) => g.title === title);
    if (!group) return;
    const groupIds = group.items.map((item) => item.id);
    setSelected((prev) => {
      if (checked) {
        const next = [...prev];
        groupIds.forEach((id) => {
          if (!next.includes(id)) next.push(id);
        });
        return next;
      }
      return prev.filter((id) => !groupIds.includes(id));
    });
  };

  const getGroupState = (
    title: string,
  ): "checked" | "unchecked" | "indeterminate" => {
    const group = permissionGroups.find((g) => g.title === title);
    if (!group) return "unchecked";
    const groupIds = group.items.map((item) => item.id);
    const count = groupIds.filter((id) => selectedSet.has(id)).length;
    if (count === 0) return "unchecked";
    if (count === groupIds.length) return "checked";
    return "indeterminate";
  };

  const handleSubmit = async () => {
    try {
      await createRole({
        name: roleName,
        description: "",
        permission_ids: selected,
      }).unwrap();
      toast.success(t("createRoleDialog.success"));
      setRoleName("");
      setSelected([]);
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(t("createRoleDialog.error"));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[calc(100dvh-1rem)] flex-col overflow-hidden sm:h-[700px] sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle>{t("createRoleDialog.title")}</DialogTitle>
        </DialogHeader>
        {/* Role Name */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">{t("createRoleDialog.nameLabel")}</label>
          <Input
            placeholder={t("createRoleDialog.namePlaceholder")}
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
          />
        </div>
        {/* Permissions - Grouped */}
        <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto">
          {permissionGroups.map((group) => {
            const groupState = getGroupState(group.title);
            const isIndeterminate = groupState === "indeterminate";
            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() =>
                      handleGroupChange(
                        group.title,
                        !isIndeterminate && groupState !== "checked",
                      )
                    }
                    className={`w-4 h-4 border rounded flex items-center justify-center ${
                      groupState === "checked"
                        ? "bg-primary border-primary"
                        : isIndeterminate
                          ? "bg-primary border-primary"
                          : "border-gray-300"
                    }`}
                  >
                    {groupState === "checked" && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                    {isIndeterminate && (
                      <div className="w-3 h-0.5 bg-white" />
                    )}
                  </button>
                  <p className="text-sm font-semibold">{group.title}</p>
                </div>
                <div className="flex flex-wrap gap-7 ml-6">
                  {group.items.map((item) => (
                    <label
                      key={item.id}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedSet.has(item.id)}
                        onCheckedChange={() => togglePermission(item.id)}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white cursor-pointer"
                      />
                      {item.description ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>{item.label}</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            {item.description}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        item.label
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {/* Footer */}
        <DialogFooter className="mt-4 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("createRoleDialog.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!roleName} loading={isLoading}>
            {t("createRoleDialog.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { mapPermissionsToGroups } from "../../infrastructure/api/mapper";
